<template>
  <section class="py-16 lg:py-24 bg-neutral-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">

        <!-- ── Left: Image + Award Badge ── -->
        <div class="relative order-2 lg:order-1">

          <!-- Main Image -->
          <div class="relative rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=650&q=80"
              alt="Real estate professionals"
              class="w-full h-80 lg:h-[480px] object-cover"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
          </div>

          <!-- Years Experience Badge — floating top-left -->
          <div class="absolute -top-5 -left-5 bg-primary-500 text-white rounded-2xl px-6 py-5 shadow-xl">
            <div class="text-4xl font-extrabold leading-none">5</div>
            <div class="text-xs font-medium mt-1 text-primary-100">Years Experience</div>
          </div>

          <!-- Award Badge — floating bottom-right -->
          <div class="absolute -bottom-5 -right-5 bg-white rounded-2xl p-4 shadow-xl border border-neutral-100 max-w-[180px]">
            <div class="flex items-center gap-2 mb-1">
              <div class="w-8 h-8 bg-accent-100 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-accent-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
              <span class="text-xs font-bold text-neutral-800">Top Platform</span>
            </div>
            <p class="text-xs text-neutral-500 leading-snug">Rwanda's #1 Digital Real Estate Platform</p>
          </div>

          <!-- Blob background -->
          <div class="absolute -z-10 -bottom-10 -left-10 w-56 h-56 bg-primary-100 rounded-full blur-3xl opacity-60"></div>
        </div>

        <!-- ── Right: Content ── -->
        <div class="order-1 lg:order-2">

          <!-- Eyebrow -->
          <p class="text-primary-500 text-sm font-semibold uppercase tracking-widest mb-3">Why Choose Us</p>

          <!-- Heading -->
          <h2 class="text-3xl md:text-4xl font-bold text-neutral-800 mb-4 leading-tight">
            Rwanda's Leading <br class="hidden md:block" />
            Digital Property Platform
          </h2>

          <p class="text-neutral-500 text-base mb-10 max-w-md leading-relaxed">
            Since 2021, we have helped thousands of buyers, renters, and investors find the right property across Rwanda. Our platform connects you with verified agents, accurate listings, and expert guidance — all in one place.
          </p>

          <!-- Feature List -->
          <div class="space-y-6 mb-10">
            <div
              v-for="feature in features"
              :key="feature.title"
              class="flex items-start gap-4"
            >
              <div class="flex-shrink-0 w-11 h-11 bg-primary-50 rounded-xl flex items-center justify-center">
                <svg class="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="feature.icon" />
                </svg>
              </div>
              <div>
                <h3 class="text-base font-bold text-neutral-800 mb-1">{{ feature.title }}</h3>
                <p class="text-sm text-neutral-500 leading-relaxed">{{ feature.description }}</p>
              </div>
            </div>
          </div>

          <!-- Phone CTA -->
          <div class="flex items-center gap-4">
            <a
              href="tel:+250789326245"
              class="inline-flex items-center gap-3 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3.5 rounded-xl font-semibold text-sm transition-colors shadow-sm"
            >
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              +250 789 326 245
            </a>
            <router-link
              to="/about"
              class="inline-flex items-center gap-2 text-sm font-semibold text-neutral-700 hover:text-primary-500 transition-colors"
            >
              Learn More
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </router-link>
          </div>

        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'

interface Feature {
  title: string
  description: string
  icon: string
}

@Options({
  data () {
    return {
      features: [
        {
          title: 'Verified Listings, Zero Surprises',
          description: 'Every property on our platform is reviewed by our team. You search with confidence knowing the listing is real, accurate, and up to date.',
          icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
        },
        {
          title: 'Network of Expert Local Agents',
          description: 'Our agents know every neighbourhood, price trend, and legal requirement across Rwanda — they work for you, not against you.',
          icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z'
        },
        {
          title: 'A Growing Property Ecosystem',
          description: 'Beyond listings — we are building tools for property management, construction reporting, and more. One platform, the full property journey.',
          icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
        }
      ] as Feature[]
    }
  }
})
export default class WhyChooseUs extends Vue {}
</script>
